import { Flavor, FlavorsSelector, IconChip, QuadStarIcon, getImageSizes } from '@ag1/ui';
import { PortableText } from '@portabletext/react';
import { productCardClickEvent } from '@portal/analytics/events/shop';
import { clickPurchaseIntentEvent } from '@portal/analytics/events/studio';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { convertCurrencyToNumber } from '@portal/shared/utils/convertCurrencyToNumber';
import { convertToKebabCase } from '@portal/shared/utils/convertToKebabCase';
import { displayShopV3PricingFormat } from '@portal/shared/utils/displayShopV3PricingFormat';
import { formatToRenderableFlavorData } from '@portal/shared/utils/shop/product/flavors';
import {
  getChargeFrequencyMetafield,
  getIsMemberOnlyMetafield,
  getLabelMetafield,
  getOriginalPriceFootnoteMetafield,
  getOriginalPriceMetafield,
  getShortDescriptionMetafield,
  getUrlMetafield
} from '@portal/shared/utils/shop/product/metafields';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { useStoreCurrency } from '@portal/state/hooks/useStoreCurrency';
import sharedStyles from '@portal/styles/shared.module.scss';
import { FormattedShopifyProduct } from '@portal/types/shopify/product.formatted.types';
import { SanityBlock, ShopV3Products, SanityKeyed } from 'types/studio/studio';

import { useIsLoggedInValue } from '@portal/appLayer/auth';
import styles from './ExploreProductCard.module.scss';

interface Props {
  showFreeProducts?: boolean;
  showMembersOnlyProducts?: boolean;
  cmsData:
    | ShopV3Products
    | {
        _type: string;
        config: {
          displayProductLabel?: boolean;
          displayProductStrikethrough?: boolean;
          displayProductFlavors?: boolean;
          displayProductSavings?: boolean;
        };
        labelList?: { labelSlug: string; labelColor: string; labelText: string }[];
        translations: {
          chargeStartingPrefix?: Array<SanityKeyed<SanityBlock>>;
          chargeFrequency?: Array<SanityKeyed<SanityBlock>>;
          discountDescriptor?: Array<SanityKeyed<SanityBlock>>;
          flavorsCounter?: Array<SanityKeyed<SanityBlock>>;
          freeLabel?: Array<SanityKeyed<SanityBlock>>;
        };
      };
  productData: FormattedShopifyProduct;
  flavorsListData?: FormattedShopifyProduct[] | undefined;
}

export const ExploreProductCard = ({
  showFreeProducts = false,
  showMembersOnlyProducts = false,
  cmsData,
  productData,
  flavorsListData
}: Props) => {
  const isLoggedIn = useIsLoggedInValue();
  const store = useStoreValue();

  const { symbolCurrency } = useStoreCurrency();

  // sanity data destructuring
  const {
    _type: sectionType,
    // IMPORTANT:
    // if "useGlobalStateManagement" is true, "/store/plpStore.ts" will be handling
    // section transitional behavior
    // if false, there is no relevant local state for this component
    config: {
      displayProductLabel,
      displayProductStrikethrough,
      displayProductSavings,
      displayProductFlavors
    },
    labelList,
    translations: {
      chargeStartingPrefix,
      chargeFrequency: cmsChargeFrequency,
      discountDescriptor,
      flavorsCounter,
      freeLabel
    }
  } = useMemo(() => cmsData, [cmsData]);

  const imageSizes = getImageSizes({ xxs: 6, s: 4, l: 3 });

  const [currentActiveProduct, setCurrentActiveProduct] = useState(productData);

  useEffect(() => {
    if (!isLoggedIn && flavorsListData?.length) {
      setCurrentActiveProduct(productData);
    }
  }, [isLoggedIn, flavorsListData, setCurrentActiveProduct, productData]);

  const { title, images, variants } = useMemo(() => currentActiveProduct, [currentActiveProduct]);

  const currentActiveVariant = useMemo(() => variants[0], [variants]);

  // product PDP url
  const productUrl = useMemo(
    () => getUrlMetafield(currentActiveProduct)?.value || '#',
    [currentActiveProduct]
  );

  // product description
  const description = useMemo(() => {
    const metaValue = getShortDescriptionMetafield(currentActiveProduct)?.value;
    return metaValue || currentActiveProduct.description;
  }, [currentActiveProduct]);

  // label data as appeared on the upper-left corner
  const { labelColor, labelText } = useMemo(() => {
    const metaValue = getLabelMetafield(currentActiveProduct)?.value;
    const labelData = metaValue
      ? labelList?.find(({ labelSlug }) => labelSlug === convertToKebabCase(metaValue))
      : undefined;

    return labelData || { labelColor: undefined, labelText: undefined };
  }, [currentActiveProduct, labelList]);

  // custom shopify charge frequency
  const customChargeFrequency = useMemo(() => {
    return getChargeFrequencyMetafield(currentActiveProduct)?.value;
  }, [currentActiveProduct]);

  // current product price
  const currentPrice = useMemo(
    () => Number(currentActiveProduct.variants[0].price.amount),
    [currentActiveProduct]
  );

  // original product price
  const originalPrice = useMemo(() => {
    const metaValue = getOriginalPriceMetafield(currentActiveProduct)?.value;
    return metaValue ? convertCurrencyToNumber(metaValue) : undefined;
  }, [currentActiveProduct]);

  // discount user receives
  const discount = useMemo(() => {
    return Number(originalPrice) - Number(currentPrice);
  }, [currentPrice, originalPrice]);

  // denotes if product is free
  // there are custom logic surrounding whether to show free products or not
  const isFreeProduct = useMemo(() => currentPrice === 0, [currentPrice]);

  // denotes if product is members only
  // there are custom logic surrounding whether to show these products or not
  const isMembersOnlyProduct = useMemo(() => {
    return getIsMemberOnlyMetafield(currentActiveProduct)?.value === 'true';
  }, [currentActiveProduct]);

  // only show striketrough original price if:
  // cms data allows it and,
  // current price is provided in metafield and is a valid number and,
  // original price is provided in metafield and is a valid number and,
  // original price greater than current price
  const showStrikethroughPrice = useMemo(() => {
    return (
      displayProductStrikethrough &&
      typeof originalPrice === 'number' &&
      !isFreeProduct &&
      originalPrice > currentPrice
    );
  }, [displayProductStrikethrough, currentPrice, originalPrice, isFreeProduct]);

  // only show discounted info if:
  // cms data allows it and,
  // original price is provided in metafield and is a valid number and,
  // original price is not 0 and,
  // original price greater than current price
  const showDiscountInfo = useMemo(() => {
    return (
      displayProductSavings &&
      typeof originalPrice === 'number' &&
      !isFreeProduct &&
      originalPrice > currentPrice
    );
  }, [displayProductSavings, currentPrice, originalPrice, isFreeProduct]);

  // original price footnote
  const originalPriceFootnote = useMemo(() => {
    return getOriginalPriceFootnoteMetafield(currentActiveProduct)?.value;
  }, [currentActiveProduct]);

  // flow flavors selectable buttons
  const showFlavors = useMemo(() => {
    return displayProductFlavors && flavorsListData !== undefined;
  }, [displayProductFlavors, flavorsListData]);

  // analytics
  const fireProductCardEvents = useCallback(() => {
    clickPurchaseIntentEvent({
      store,
      clickText: labelText,
      location: sectionType,
      url: productUrl
    });
    productCardClickEvent({
      productId: productData.id,
      productName: title,
      productPrice: currentPrice,
      productVariantId: currentActiveVariant.id,
      productVariant: currentActiveVariant.title,
      location: sectionType
    });
  }, [
    sectionType,
    labelText,
    store,
    productUrl,
    productData.id,
    title,
    currentPrice,
    currentActiveVariant.id,
    currentActiveVariant.title
  ]);

  const onFlavorClick = useCallback(
    (selectedFlavor: Flavor) => {
      if (currentActiveProduct.id === selectedFlavor.id) return;

      const newFlavorProduct = flavorsListData?.find(
        (flavorProductData) => flavorProductData.id === selectedFlavor.id
      ) as FormattedShopifyProduct;

      setCurrentActiveProduct(newFlavorProduct);
    },
    [currentActiveProduct, flavorsListData, setCurrentActiveProduct]
  );

  // do not show free products unless parent allows it
  // do not show members only products unless parent allows it
  if ((!showFreeProducts && isFreeProduct) || (!showMembersOnlyProducts && isMembersOnlyProduct)) {
    return null;
  }

  return (
    <Link href={productUrl} className={styles.productCard} onClick={fireProductCardEvents}>
      <div className={styles.imageContainer}>
        <Image
          src={images[0]?.src}
          alt={images[0]?.altText || ''}
          width={400}
          height={400}
          sizes={imageSizes}
          className={styles.productImage}
        />
        {displayProductLabel && labelColor && labelText && (
          <IconChip className={styles.productLabel} fontColor="#000" backgroundColor={labelColor}>
            <QuadStarIcon />
            <span>{labelText}</span>
          </IconChip>
        )}
      </div>
      <div className={styles.infoContainer}>
        <h5 className={styles.productName}>{title}</h5>
        <p className={styles.productDescription}>{description}</p>
        <div
          className={clsx(
            styles.pricing,
            !showFlavors &&
              showStrikethroughPrice &&
              originalPriceFootnote &&
              sharedStyles.zeroBottomMargins
          )}>
          <div>
            <span className={styles.discountedPrice}>
              {!isFreeProduct ? (
                <>
                  {originalPrice && chargeStartingPrefix && (
                    <span className={sharedStyles.spanFragment}>
                      <PortableText value={chargeStartingPrefix} />
                    </span>
                  )}
                  {originalPrice && chargeStartingPrefix && ' '}
                  {displayShopV3PricingFormat(symbolCurrency, currentPrice)}
                  {originalPrice && (customChargeFrequency || cmsChargeFrequency) && '/'}
                  {originalPrice && customChargeFrequency && (
                    <span className={sharedStyles.spanFragment}>{customChargeFrequency}</span>
                  )}
                  {originalPrice && !customChargeFrequency && cmsChargeFrequency && (
                    <span className={sharedStyles.spanFragment}>
                      <PortableText value={cmsChargeFrequency} />
                    </span>
                  )}
                </>
              ) : (
                freeLabel && <PortableText value={freeLabel} />
              )}
            </span>
            {showStrikethroughPrice && (
              <span className={styles.originalPrice}>
                {displayShopV3PricingFormat(symbolCurrency, originalPrice)}
                {originalPriceFootnote && <sup className={styles.superscript}>†</sup>}
              </span>
            )}
          </div>
          {showDiscountInfo && (
            <div className={styles.savingsAmount}>
              {displayShopV3PricingFormat(symbolCurrency, discount)}{' '}
              {discountDescriptor && (
                <span className={sharedStyles.spanFragment}>
                  <PortableText value={discountDescriptor} />
                </span>
              )}
            </div>
          )}
        </div>
        {showFlavors && flavorsListData?.length && flavorsCounter && (
          <FlavorsSelector
            translations={{ flavorsCounter }}
            flavors={formatToRenderableFlavorData(flavorsListData)}
            onFlavorClick={onFlavorClick}
          />
        )}
      </div>
    </Link>
  );
};
